import { useState, useEffect, useRef, useCallback } from "react";

// ============================================================
// DATA
// ============================================================
const faqs = [
  {
    category: "Booking Process",
    icon: "📋",
    color: "#e91e8c",
    questions: [
      {
        q: "How do I book a decorator on DecorHub?",
        a: "Booking on DecorHub is simple! Browse decorators in your city, check their portfolio and reviews, then click 'Book Now'. Fill in your event details — date, venue, type of decoration — and confirm. The decorator will contact you within 24 hours to finalize everything.",
        tags: ["Booking", "Getting Started"],
      },
      {
        q: "How to find the best decorator near me in Raipur, Chhattisgarh?",
        a: "Just open DecorHub, allow location access or select your city (Raipur, Bilaspur, Durg, Bhilai etc.), and browse verified decorators near you. You can filter by event type, budget, and ratings to find the perfect decorator near you in CG.",
        tags: ["Near Me", "Raipur", "CG"],
      },
      {
        q: "Can I book a decorator for same-day or last-minute events?",
        a: "Yes! DecorHub has decorators available for urgent and last-minute bookings in Raipur and across Chhattisgarh. Use the 'Available Today' filter to find decorators who can take immediate bookings.",
        tags: ["Urgent", "Same Day"],
      },
      {
        q: "How far in advance should I book a decorator?",
        a: "For weddings and large events, we recommend booking at least 2-4 weeks in advance, especially during peak season (October to February). For birthday parties and small events, 3-7 days advance booking is usually sufficient.",
        tags: ["Planning", "Timeline"],
      },
    ],
  },
  {
    category: "Pricing & Payment",
    icon: "💰",
    color: "#00d4aa",
    questions: [
      {
        q: "How much does event decoration cost in Raipur, CG?",
        a: "Decoration costs in Raipur, Chhattisgarh vary based on event type and scale. Birthday decorations start from ₹2,000–5,000, wedding decorations from ₹15,000–50,000+, and corporate events from ₹10,000 onwards. On DecorHub you can compare prices from multiple decorators and choose the best one within your budget.",
        tags: ["Pricing", "Budget"],
      },
      {
        q: "Is DecorHub free to use for customers?",
        a: "Yes! Browsing and finding decorators on DecorHub is completely FREE for customers. You only pay the decorator's service charges directly. There are no hidden fees or platform charges for customers.",
        tags: ["Free", "No Hidden Fees"],
      },
      {
        q: "What payment methods are accepted?",
        a: "Most decorators on DecorHub accept UPI (GPay, PhonePe, Paytm), bank transfer, and cash. Payment terms are agreed directly between you and the decorator. We recommend paying a small advance to confirm the booking.",
        tags: ["UPI", "Payment"],
      },
      {
        q: "What if I want to cancel my booking?",
        a: "Cancellation policies vary by decorator. Most decorators allow free cancellation up to 48–72 hours before the event. Check the decorator's cancellation policy on their profile before booking. DecorHub also helps mediate in case of disputes.",
        tags: ["Cancellation", "Refund"],
      },
    ],
  },
  {
    category: "Decorator Verification",
    icon: "✅",
    color: "#7c3aed",
    questions: [
      {
        q: "Are all decorators on DecorHub verified?",
        a: "Yes! Every decorator on DecorHub goes through a verification process. We check their identity, past work portfolio, and customer reviews before approving their listing. Verified decorators have a blue checkmark on their profile so you can book with confidence.",
        tags: ["Verified", "Trust", "Safety"],
      },
      {
        q: "How are decorators rated on DecorHub?",
        a: "After every event, customers can leave a star rating and written review for the decorator. Ratings are based on quality of work, punctuality, behavior, and value for money. Only genuine customers who have booked through DecorHub can leave reviews.",
        tags: ["Ratings", "Reviews"],
      },
      {
        q: "What if the decorator doesn't show up or does poor work?",
        a: "DecorHub takes quality seriously. If a decorator fails to show up or delivers poor quality work, contact our support team immediately. We will help you find an alternative decorator and assist with refund or resolution as per our policy.",
        tags: ["Support", "Refund", "Quality"],
      },
      {
        q: "How do I become a decorator on DecorHub?",
        a: "If you are an event decorator in Chhattisgarh, you can register on DecorHub for FREE. Upload your portfolio, set your service area and pricing, and start receiving bookings. Click on 'Register as Decorator' to get started today!",
        tags: ["Join", "Register", "Decorator"],
      },
    ],
  },
  {
    category: "Cities & Coverage",
    icon: "📍",
    color: "#f59e0b",
    questions: [
      {
        q: "Which cities in Chhattisgarh does DecorHub cover?",
        a: "DecorHub currently covers Raipur, Bilaspur, Durg, Bhilai, Korba, Jagdalpur, Rajnandgaon, and Ambikapur. We are rapidly expanding to more cities across Chhattisgarh. If your city is not listed yet, contact us and we will prioritize it!",
        tags: ["Cities", "CG", "Coverage"],
      },
      {
        q: "Is DecorHub available outside Raipur in CG?",
        a: "Yes! DecorHub is available across Chhattisgarh. We have decorators listed in Bilaspur, Durg, Bhilai, Korba, and more. Select your city on the platform to find the best decorators near you.",
        tags: ["Bilaspur", "Durg", "Bhilai"],
      },
      {
        q: "Can a decorator travel to my location for the event?",
        a: "Many decorators on DecorHub offer outstation services and can travel to nearby cities and towns. Check the decorator's service area on their profile, or contact them directly to discuss travel arrangements.",
        tags: ["Travel", "Outstation"],
      },
    ],
  },
  {
    category: "Event Types",
    icon: "🎉",
    color: "#ef4444",
    questions: [
      {
        q: "What types of events can I find decorators for on DecorHub?",
        a: "DecorHub has decorators for all types of events — weddings, birthday parties, anniversary celebrations, baby showers, griha pravesh (housewarming), corporate events, product launches, engagement ceremonies, and more. Whatever your event, we have the right decorator for you in CG.",
        tags: ["Wedding", "Birthday", "Corporate"],
      },
      {
        q: "Can I find wedding decorators near me in Chhattisgarh?",
        a: "Absolutely! DecorHub has a large network of experienced wedding decorators across Chhattisgarh. From mandap decoration and flower arrangements to stage decoration and lighting — find and book the best wedding decorators near you in Raipur and CG.",
        tags: ["Wedding", "Mandap", "Flowers"],
      },
      {
        q: "Do decorators provide their own materials and supplies?",
        a: "Yes, most decorators on DecorHub bring their own decoration materials including flowers, balloons, lights, fabric, and props. You can discuss specific requirements like custom themes or special materials directly with the decorator before booking.",
        tags: ["Materials", "Supplies", "Custom"],
      },
    ],
  },
];

const allTags = [...new Set(faqs.flatMap((c) => c.questions.flatMap((q) => q.tags)))];

const generateFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.flatMap((cat) =>
    cat.questions.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    }))
  ),
});

// ============================================================
// ANIMATED COUNTER
// ============================================================
function AnimatedStat({ value, label, color }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const end = parseInt(value);
          const step = Math.ceil(end / 60);
          const timer = setInterval(() => {
            start = Math.min(start + step, end);
            setCount(start);
            if (start >= end) clearInterval(timer);
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return (
    <div ref={ref} style={{ textAlign: "center", padding: "18px 24px" }}>
      <div style={{ fontSize: "clamp(26px,4vw,44px)", fontWeight: "900", color, fontFamily: "'Clash Display', sans-serif" }}>
        {count}+
      </div>
      <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", marginTop: "4px", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: "600" }}>
        {label}
      </div>
    </div>
  );
}

// ============================================================
// CANVAS PARTICLES
// ============================================================
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 70 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.8 + 0.3,
      dx: (Math.random() - 0.5) * 0.35,
      dy: (Math.random() - 0.5) * 0.35,
      alpha: Math.random() * 0.4 + 0.05,
      color: Math.random() > 0.5 ? "233,30,140" : "124,58,237",
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

// ============================================================
// MAIN
// ============================================================
export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [helpfulMap, setHelpfulMap] = useState({});

  useEffect(() => {
    document.title = "FAQ — Best Decorators Near Me in Raipur CG | DecorHub";
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.content = "Got questions about booking event decorators in Raipur, Chhattisgarh? Find answers about pricing, booking process, verified decorators & cities covered on DecorHub CG.";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.text = JSON.stringify(generateFAQSchema());
    document.head.appendChild(script);
    return () => { const s = document.getElementById("faq-schema"); if (s) s.remove(); };
  }, []);

  const toggle = useCallback((id) => setOpenIndex((p) => (p === id ? null : id)), []);
  const markHelpful = useCallback((id, val) => setHelpfulMap((p) => ({ ...p, [id]: val })), []);

  const filtered = faqs.map((section) => ({
    ...section,
    questions: section.questions.filter((faq) => {
      const matchCat = activeCategory === "All" || section.category === activeCategory;
      const matchSearch = !searchQuery || faq.q.toLowerCase().includes(searchQuery.toLowerCase()) || faq.a.toLowerCase().includes(searchQuery.toLowerCase());
      const matchTag = !activeTag || faq.tags.includes(activeTag);
      return matchCat && matchSearch && matchTag;
    }),
  })).filter((s) => s.questions.length > 0);

  const totalResults = filtered.reduce((a, s) => a + s.questions.length, 0);

  return (
    <div style={S.page}>
      <style>{CSS}</style>

      {/* HERO */}
      <div style={S.hero}>
        <Particles />
        <div style={S.meshGlow1} /><div style={S.meshGlow2} />
        <div style={S.heroInner}>
          <div className="fiu" style={{ "--d": "0s" }}>
            <span style={S.badge}><span style={S.dot} />Help Center</span>
          </div>
          <h1 className="fiu" style={{ "--d": "0.1s", ...S.heroTitle }}>
            Frequently Asked<br />
            <span style={S.accent}>Questions</span>
          </h1>
          <p className="fiu" style={{ "--d": "0.2s", ...S.heroSub }}>
            Everything you need to know about finding & booking the best event decorators near you in Raipur, Chhattisgarh
          </p>

          {/* Search */}
          <div className="fiu" style={{ "--d": "0.3s", ...S.searchWrap }}>
            <span style={{ fontSize: "18px" }}>🔍</span>
            <input
              style={S.searchInput}
              placeholder="Search questions... e.g. 'price', 'Raipur', 'wedding'"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setOpenIndex(null); }}
            />
            {searchQuery && (
              <button style={S.clearBtn} onClick={() => setSearchQuery("")}>✕</button>
            )}
          </div>

          {/* Stats */}
          <div className="fiu" style={{ "--d": "0.4s", ...S.statsRow }}>
            <AnimatedStat value={18} label="Questions" color="#e91e8c" />
            <span style={S.statDiv} />
            <AnimatedStat value={5} label="Categories" color="#00d4aa" />
            <span style={S.statDiv} />
            <AnimatedStat value={8} label="Cities" color="#7c3aed" />
            <span style={S.statDiv} />
            <AnimatedStat value={500} label="Happy Clients" color="#f59e0b" />
          </div>
        </div>
      </div>

      {/* BODY */}
      <div style={S.body}>
        <div style={S.layout}>

          {/* SIDEBAR */}
          <aside style={S.sidebar}>
            <div style={S.card}>
              <p style={S.cardLabel}>Categories</p>
              {["All", ...faqs.map((f) => f.category)].map((cat) => {
                const sec = faqs.find((f) => f.category === cat);
                const active = activeCategory === cat;
                return (
                  <button key={cat} onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                    style={{ ...S.catBtn, ...(active ? { background: `${sec?.color || "#e91e8c"}18`, borderColor: sec?.color || "#e91e8c", color: "#fff" } : {}) }}>
                    <span>{sec?.icon || "✨"}</span>
                    <span style={{ flex: 1, textAlign: "left" }}>{cat}</span>
                    <span style={{ ...S.countBadge, background: active ? (sec?.color || "#e91e8c") : "rgba(255,255,255,0.07)" }}>
                      {cat === "All" ? faqs.reduce((a, f) => a + f.questions.length, 0) : sec?.questions.length}
                    </span>
                  </button>
                );
              })}
            </div>

            <div style={{ ...S.card, marginTop: "14px" }}>
              <p style={S.cardLabel}>Popular Tags</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                {allTags.map((tag) => (
                  <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                    style={{ ...S.tagBtn, ...(activeTag === tag ? { background: "rgba(233,30,140,0.15)", borderColor: "rgba(233,30,140,0.5)", color: "#e91e8c" } : {}) }}>
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            <div style={S.helpCard}>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>💬</div>
              <p style={{ fontWeight: "700", fontSize: "15px", marginBottom: "6px" }}>Still have questions?</p>
              <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", marginBottom: "18px", lineHeight: "1.6" }}>Our support team is available 24/7 to help you.</p>
              <a href="/contact" style={S.helpBtn}>Contact Support →</a>
            </div>
          </aside>

          {/* MAIN */}
          <main style={S.main}>
            <div style={S.resultsBar}>
              <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.35)" }}>
                {searchQuery || activeTag ? `${totalResults} result${totalResults !== 1 ? "s" : ""} found` : `Showing ${totalResults} questions`}
              </span>
              {(searchQuery || activeTag || activeCategory !== "All") && (
                <button onClick={() => { setSearchQuery(""); setActiveTag(null); setActiveCategory("All"); setOpenIndex(null); }} style={S.clearFilters}>
                  Clear all ✕
                </button>
              )}
            </div>

            {filtered.length === 0 && (
              <div style={S.empty}>
                <div style={{ fontSize: "52px", marginBottom: "16px" }}>🤔</div>
                <p style={{ fontSize: "20px", fontWeight: "700", marginBottom: "8px" }}>No results found</p>
                <p style={{ color: "rgba(255,255,255,0.4)" }}>Try a different keyword or clear your filters.</p>
              </div>
            )}

            {filtered.map((section, sIdx) => (
              <div key={sIdx} style={S.section} className="fiu">
                <div style={S.secHeader}>
                  <div style={{ ...S.secIconBox, background: `${section.color}18`, border: `1px solid ${section.color}33` }}>
                    <span style={{ fontSize: "22px" }}>{section.icon}</span>
                  </div>
                  <div>
                    <h2 style={S.secTitle}>{section.category}</h2>
                    <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", marginTop: "2px" }}>{section.questions.length} questions</p>
                  </div>
                  <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${section.color}55, transparent)`, marginLeft: "16px" }} />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {section.questions.map((faq, qIdx) => {
                    const id = `${sIdx}-${qIdx}`;
                    const isOpen = openIndex === id;
                    const helpful = helpfulMap[id];
                    return (
                      <div key={qIdx} className="faq-card"
                        style={{ ...S.faqCard, ...(isOpen ? { borderColor: `${section.color}55`, boxShadow: `0 4px 40px ${section.color}12`, background: "rgba(255,255,255,0.05)" } : {}) }}>
                        <div style={S.faqRow} onClick={() => toggle(id)}>
                          <div style={{ ...S.qNum, color: section.color, borderColor: `${section.color}44` }}>{qIdx + 1}</div>
                          <span style={S.qText}>{faq.q}</span>
                          <span style={{ ...S.chevron, ...(isOpen ? { transform: "rotate(180deg)", color: section.color } : {}) }}>▾</span>
                        </div>

                        <div style={{ maxHeight: isOpen ? "600px" : 0, overflow: "hidden", transition: "max-height 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                          <div style={{ display: "flex", gap: "14px", padding: "0 20px 20px", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "16px" }}>
                            <div style={{ width: "3px", borderRadius: "3px", background: section.color, flexShrink: 0, opacity: 0.7 }} />
                            <div style={{ flex: 1 }}>
                              <p style={S.aText}>{faq.a}</p>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "14px" }}>
                                {faq.tags.map((t) => (
                                  <span key={t} onClick={(e) => { e.stopPropagation(); setActiveTag(t); }}
                                    style={{ ...S.tagBtn, fontSize: "11px", cursor: "pointer" }}>#{t}</span>
                                ))}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "16px", paddingTop: "14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)" }}>Was this helpful?</span>
                                {helpful === undefined ? (
                                  <>
                                    <button style={S.hBtn} onClick={(e) => { e.stopPropagation(); markHelpful(id, true); }}>👍 Yes</button>
                                    <button style={S.hBtn} onClick={(e) => { e.stopPropagation(); markHelpful(id, false); }}>👎 No</button>
                                  </>
                                ) : (
                                  <span style={{ fontSize: "13px", fontWeight: "600", color: helpful ? "#00d4aa" : "#e91e8c" }}>
                                    {helpful ? "✓ Thanks!" : "Sorry! Contact us for help."}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* CTA */}
            <div style={S.cta}>
              <div style={S.ctaGlow} />
              <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                <div style={{ fontSize: "52px", marginBottom: "18px" }}>🎨</div>
                <h3 style={{ fontFamily: "'Clash Display',sans-serif", fontSize: "clamp(22px,4vw,36px)", fontWeight: "800", marginBottom: "12px" }}>
                  Ready to book your decorator?
                </h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", lineHeight: "1.75", marginBottom: "32px", maxWidth: "500px", margin: "0 auto 32px" }}>
                  Find & book the best event decorators near you in Raipur, Chhattisgarh — 100% verified, best prices guaranteed.
                </p>
                <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
                  <a href="/decorators" style={S.ctaPrimary}>Find Decorators Near Me →</a>
                  <a href="/decorators?register=true" style={S.ctaSecondary}>Join as Decorator</a>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CSS
// ============================================================
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; }
  .fiu { opacity: 0; transform: translateY(22px); animation: fiu 0.6s ease var(--d, 0s) forwards; }
  @keyframes fiu { to { opacity: 1; transform: translateY(0); } }
  .faq-card { transition: border-color 0.3s, box-shadow 0.3s, transform 0.2s, background 0.3s; }
  .faq-card:hover { transform: translateY(-2px); }
  input::placeholder { color: rgba(255,255,255,0.25); }
  input:focus { outline: none; }
  button { border: none; cursor: pointer; transition: all 0.2s; }
  a { text-decoration: none; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: #080810; }
  ::-webkit-scrollbar-thumb { background: rgba(233,30,140,0.3); border-radius: 3px; }
  @media (max-width: 800px) { .desktop-sidebar { display: none !important; } }
`;

// ============================================================
// STYLES
// ============================================================
const S = {
  page: { minHeight: "100vh", background: "#080810", color: "#fff", fontFamily: "'DM Sans', sans-serif" },
  hero: { position: "relative", overflow: "hidden", background: "linear-gradient(135deg,#0c0018 0%,#08081a 45%,#001018 100%)", padding: "110px 20px 80px", textAlign: "center", borderBottom: "1px solid rgba(233,30,140,0.12)" },
  meshGlow1: { position: "absolute", top: "-100px", left: "-100px", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(233,30,140,0.12) 0%, transparent 70%)", pointerEvents: "none" },
  meshGlow2: { position: "absolute", bottom: "-80px", right: "-80px", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)", pointerEvents: "none" },
  heroInner: { position: "relative", zIndex: 1, maxWidth: "780px", margin: "0 auto" },
  badge: { display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(233,30,140,0.08)", border: "1px solid rgba(233,30,140,0.25)", color: "#e91e8c", padding: "7px 20px", borderRadius: "50px", fontSize: "11px", fontWeight: "700", letterSpacing: "2.5px", textTransform: "uppercase", marginBottom: "26px" },
  dot: { width: "7px", height: "7px", borderRadius: "50%", background: "#e91e8c", boxShadow: "0 0 10px #e91e8c", display: "inline-block" },
  heroTitle: { fontFamily: "'Clash Display',sans-serif", fontSize: "clamp(38px,7vw,74px)", fontWeight: "800", lineHeight: "1.08", marginBottom: "22px", color: "#fff" },
  accent: { background: "linear-gradient(135deg,#e91e8c,#ff6b35)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  heroSub: { fontSize: "clamp(14px,2vw,17px)", color: "rgba(255,255,255,0.5)", lineHeight: "1.85", marginBottom: "38px" },
  searchWrap: { display: "flex", alignItems: "center", gap: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50px", padding: "14px 22px", maxWidth: "540px", margin: "0 auto 44px", backdropFilter: "blur(12px)" },
  searchInput: { flex: 1, background: "none", border: "none", color: "#fff", fontSize: "15px", fontFamily: "'DM Sans',sans-serif" },
  clearBtn: { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", borderRadius: "50%", width: "24px", height: "24px", fontSize: "11px", display: "flex", alignItems: "center", justifyContent: "center" },
  statsRow: { display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "20px", overflow: "hidden" },
  statDiv: { width: "1px", height: "44px", background: "rgba(255,255,255,0.07)", flexShrink: 0 },
  body: { maxWidth: "1220px", margin: "0 auto", padding: "52px 20px 90px" },
  layout: { display: "flex", gap: "30px", alignItems: "flex-start" },
  sidebar: { width: "272px", flexShrink: 0, position: "sticky", top: "20px" },
  card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "20px" },
  cardLabel: { fontSize: "10px", fontWeight: "700", letterSpacing: "2.5px", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "14px" },
  catBtn: { display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "10px 12px", borderRadius: "10px", background: "none", color: "rgba(255,255,255,0.6)", fontSize: "13px", fontWeight: "500", marginBottom: "4px", border: "1px solid transparent" },
  countBadge: { fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "20px", color: "#fff" },
  tagBtn: { padding: "5px 11px", borderRadius: "20px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.5)", fontSize: "12px", fontWeight: "500" },
  helpCard: { marginTop: "14px", background: "linear-gradient(135deg,rgba(233,30,140,0.08),rgba(124,58,237,0.08))", border: "1px solid rgba(233,30,140,0.18)", borderRadius: "16px", padding: "26px 20px", textAlign: "center" },
  helpBtn: { display: "inline-block", background: "#e91e8c", color: "#fff", padding: "10px 22px", borderRadius: "25px", fontSize: "13px", fontWeight: "700" },
  main: { flex: 1, minWidth: 0 },
  resultsBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "28px", paddingBottom: "16px", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  clearFilters: { background: "rgba(233,30,140,0.1)", color: "#e91e8c", border: "1px solid rgba(233,30,140,0.25)", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" },
  empty: { textAlign: "center", padding: "90px 20px", color: "rgba(255,255,255,0.45)" },
  section: { marginBottom: "50px" },
  secHeader: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" },
  secIconBox: { width: "50px", height: "50px", borderRadius: "13px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  secTitle: { fontFamily: "'Clash Display',sans-serif", fontSize: "20px", fontWeight: "700" },
  faqCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px", overflow: "hidden" },
  faqRow: { display: "flex", alignItems: "center", gap: "14px", padding: "18px 20px", cursor: "pointer" },
  qNum: { width: "28px", height: "28px", borderRadius: "8px", border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "800", flexShrink: 0 },
  qText: { flex: 1, fontSize: "15px", fontWeight: "600", color: "#fff", lineHeight: "1.5" },
  chevron: { fontSize: "20px", color: "rgba(255,255,255,0.4)", transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1), color 0.2s", flexShrink: 0 },
  aText: { fontSize: "14px", color: "rgba(255,255,255,0.62)", lineHeight: "1.9" },
  hBtn: { padding: "5px 14px", borderRadius: "20px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)", fontSize: "12px" },
  cta: { position: "relative", overflow: "hidden", marginTop: "64px", background: "linear-gradient(135deg,rgba(233,30,140,0.1),rgba(124,58,237,0.1))", border: "1px solid rgba(233,30,140,0.18)", borderRadius: "24px", padding: "64px 40px" },
  ctaGlow: { position: "absolute", top: "-80px", left: "50%", transform: "translateX(-50%)", width: "400px", height: "250px", background: "radial-gradient(circle,rgba(233,30,140,0.2),transparent 70%)", pointerEvents: "none" },
  ctaPrimary: { display: "inline-block", background: "linear-gradient(135deg,#e91e8c,#ff6b35)", color: "#fff", padding: "14px 32px", borderRadius: "50px", fontWeight: "700", fontSize: "15px", boxShadow: "0 8px 30px rgba(233,30,140,0.3)" },
  ctaSecondary: { display: "inline-block", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", color: "#fff", padding: "14px 32px", borderRadius: "50px", fontWeight: "600", fontSize: "15px" },
};
